import { useState, type ReactNode } from 'react';
import { useApi, apiPost, Cargando, ErrorApi, BotonAccion, confirmar, formatFecha } from '../shared/ui.tsx';
import { formatoRango } from '../shared/horas.ts';

interface ServicioHoy {
  convocatoria_id: string;
  hora_inicio: string;
  hora_fin: string;
  salon_nombre: string;
  cliente_nombre: string;
  tiene_ubicacion: boolean;
  llegada_en: string | null;
  salida_en: string | null;
  fuera_de_rango: boolean | null;
}

interface Marca {
  id: string;
  fecha: string;
  hora_inicio: string;
  hora_fin: string;
  salon_nombre: string;
  cliente_nombre: string;
  llegada_en: string;
  salida_en: string | null;
  metodo: string | null;
  fuera_de_rango: number;
}

function horaLocal(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString('es-UY', { hour: '2-digit', minute: '2-digit', hour12: false });
}

function obtenerUbicacion(): Promise<{ latitud: number; longitud: number }> {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Este navegador no soporta ubicación'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ latitud: pos.coords.latitude, longitud: pos.coords.longitude }),
      () => reject(new Error('No se pudo obtener tu ubicación. Permitila en el navegador.')),
      { enableHighAccuracy: true, timeout: 15000 },
    );
  });
}

export function AsistenciaPage(): ReactNode {
  const { datos, cargando, error, recargar } = useApi<{ servicio: ServicioHoy | null; servicios?: ServicioHoy[] }>('/api/asistencia/hoy');
  const historial = useApi<Marca[]>('/api/asistencia/historial');
  const [marcando, setMarcando] = useState(false);

  async function marcar(tipo: 'llegada' | 'salida', convocatoriaId?: string, esCorreccion = false) {
    const pregunta = esCorreccion
      ? `Vas a REEMPLAZAR la marca de ${tipo} anterior por la hora actual. ¿Seguro?`
      : tipo === 'llegada'
        ? '¿Marcar tu llegada? Se registra la hora y tu ubicación como inicio del servicio.'
        : '¿Marcar tu salida? Se registra la hora y tu ubicación como fin del servicio.';
    if (!confirmar(pregunta)) return;
    setMarcando(true);
    try {
      const ubic = await obtenerUbicacion();
      const r = await apiPost<{ ok: boolean; fuera_de_rango?: boolean }>(
        `/api/asistencia/${tipo}`,
        convocatoriaId ? { ...ubic, convocatoria_id: convocatoriaId } : ubic,
      );
      if (tipo === 'llegada' && r.fuera_de_rango) {
        alert('Llegada registrada. Ojo: el GPS te ubica fuera del rango del salón; quedó registrado igual.');
      }
      recargar();
      historial.recargar();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Error');
    } finally {
      setMarcando(false);
    }
  }

  if (cargando) return <Cargando />;
  if (error) return <ErrorApi mensaje={error} onReintentar={recargar} />;

  const servicios = datos?.servicios ?? (datos?.servicio ? [datos.servicio] : []);
  const sinLlegada = servicios.filter((sv) => !sv.llegada_en);
  const abiertos = servicios.filter((sv) => sv.llegada_en && !sv.salida_en);
  const todoCompleto = servicios.length > 0 && sinLlegada.length === 0 && abiertos.length === 0;

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold text-neutro-800">Asistencia</h2>

      {servicios.length > 0 ? (
        <section className="rounded-2xl bg-white p-5 shadow-sm border border-contorno-var">
          <p className="text-xs font-bold uppercase tracking-wide text-primario-400">
            {servicios.length === 1 ? 'Tu servicio de hoy' : `Tus ${servicios.length} servicios de hoy`}
          </p>

          {servicios.map((sv) => (
            <div key={sv.convocatoria_id} className="mt-3 border-t border-superficie-200 pt-3 first:border-0">
              <p className="text-lg font-bold text-neutro-800">{sv.cliente_nombre} · {sv.salon_nombre}</p>
              <p className="text-sm text-neutro-500">{formatoRango(sv.hora_inicio, sv.hora_fin)}</p>

              <div className="mt-3 grid grid-cols-2 gap-3">
                <div className="rounded-xl bg-superficie-100/60 p-3">
                  <p className="text-xs font-semibold text-neutro-400">Llegada</p>
                  <p className={`mt-1 text-lg font-bold ${sv.llegada_en ? 'text-estado-ok' : 'text-neutro-400'}`}>
                    {sv.llegada_en ? `✓ ${horaLocal(sv.llegada_en)}` : '—'}
                  </p>
                </div>
                <div className="rounded-xl bg-superficie-100/60 p-3">
                  <p className="text-xs font-semibold text-neutro-400">Salida</p>
                  <p className={`mt-1 text-lg font-bold ${sv.salida_en ? 'text-estado-ok' : 'text-neutro-400'}`}>
                    {sv.salida_en ? `✓ ${horaLocal(sv.salida_en)}` : '—'}
                  </p>
                </div>
              </div>
              {sv.fuera_de_rango === true && (
                <p className="mt-2 text-xs font-semibold text-estado-pendiente">⚠ La llegada quedó marcada fuera del rango del salón</p>
              )}
              {(sv.llegada_en || sv.salida_en) && (
                <p className="mt-2 text-xs text-neutro-400">
                  ¿Te equivocaste?{' '}
                  {sv.llegada_en && (
                    <button
                      className="font-semibold text-primario-400 underline hover:no-underline disabled:opacity-50"
                      disabled={marcando}
                      onClick={() => void marcar('llegada', sv.convocatoria_id, true)}
                    >
                      ↻ Corregir llegada
                    </button>
                  )}
                  {sv.llegada_en && sv.salida_en && ' · '}
                  {sv.salida_en && (
                    <button
                      className="font-semibold text-primario-400 underline hover:no-underline disabled:opacity-50"
                      disabled={marcando}
                      onClick={() => void marcar('salida', sv.convocatoria_id, true)}
                    >
                      ↻ Corregir salida
                    </button>
                  )}
                </p>
              )}
            </div>
          ))}

          <div className="mt-4 space-y-2">
            {abiertos.length > 0 && (
              <BotonAccion onClick={() => void marcar('salida')} disabled={marcando} className="w-full bg-amber-600 text-white">
                {marcando ? 'Marcando…' : '🚪 Marcar salida'}
              </BotonAccion>
            )}
            {sinLlegada.length > 0 && (
              <BotonAccion onClick={() => void marcar('llegada')} disabled={marcando} className="w-full">
                {marcando ? 'Marcando…' : servicios.length > sinLlegada.length ? '📍 Marcar llegada del siguiente' : '📍 Marcar llegada'}
              </BotonAccion>
            )}
            {todoCompleto && (
              <p className="text-center text-sm font-bold text-estado-ok">✓ Asistencia completa. ¡Buen trabajo!</p>
            )}
          </div>
        </section>
      ) : (
        <section className="rounded-2xl bg-white p-5 text-center shadow-sm border border-contorno-var">
          <p className="font-semibold text-neutro-800">Sin servicio hoy</p>
          <p className="mt-1 text-sm text-neutro-400">Cuando tengas un servicio asignado para hoy, acá vas a poder marcar tu llegada y salida.</p>
        </section>
      )}

      {historial.datos && historial.datos.length > 0 && (
        <section className="rounded-2xl bg-white p-5 shadow-sm border border-contorno-var">
          <p className="text-xs font-bold uppercase tracking-wide text-primario-400">Tus marcas anteriores</p>
          <ul className="mt-2 divide-y divide-superficie-200">
            {historial.datos.slice(0, 30).map((m) => (
              <li key={m.id} className="py-2.5 text-sm">
                <p className="font-semibold text-neutro-800">{formatFecha(m.fecha)} · {m.cliente_nombre} · {m.salon_nombre}</p>
                <p className="mt-0.5 text-xs text-neutro-500">
                  Entrada ✓ {horaLocal(m.llegada_en)}
                  {m.salida_en ? ` · Salida ✓ ${horaLocal(m.salida_en)}` : ' · Sin salida'}
                  {m.fuera_de_rango === 1 ? ' · ⚠ fuera de rango' : ''}
                </p>
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="rounded-xl bg-primario-400/10 p-4 text-sm text-primario-500">
        ⓘ La llegada y la salida se validan con tu ubicación GPS contra el salón del servicio. Permití la ubicación cuando el navegador la pida.
      </div>
    </div>
  );
}
